import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import {
  Button,
  BUTTON_THEME,
  Icon,
  IconButton,
  Select,
  CheckBox,
} from '@st-operations/uikit/src/v2'
import format from '@st-operations/uikit/lib/format'
import Events from '@st-operations/uikit/Events'

import { CHECKBOX_ACTION } from '../table'

import './styles.scss'

export const ACTIONS = {
  CLONE: 'clone',
  CLONE_WITH_AUDIENCES: 'clone-with-audiences',
  CLONE_RESOURCE_OPTIMIZED: 'clone-resource-optimized',
  APPROVE: 'approve',
  ARCHIVE: 'archive',
  MERGE: 'merge',
  DELETE: 'delete',
  EXPORT: 'export',
  BULK_EDIT: 'bulk-edit',
  // BATCH_PREBUILT_ATTRIBUTE_SETUP: 'batch-prebuilt-attribute',
}

export const clearBatchSidebar = () => Events.emit('ListView-selection--clear')

const BatchCard = ({
  id,
  title = '-',
  status = '-',
  size,
  date = '-',
  user = '-',
  markedForRemoval,
  handleRemove,
  action,
  showGeoAudienceCheck,
  geoAudience,
  handleGeoSearchClicked,
}) => {
  const [geoSearch, setGeoSearch] = useState(false)

  useEffect(() => {
    setGeoSearch(geoAudience)
  }, [geoAudience])

  const handleGeoAudienceCheckBoxClick = () => {
    const status = !geoSearch
    setGeoSearch(status)
    handleGeoSearchClicked(id, status)
  }

  return (
    <div
      className={classNames('batch-card', { 'batch-card--marked-to-removal': markedForRemoval })}
    >
      <header className="batch-card__header">
        <p>{title}</p>
        <IconButton
          onClick={handleRemove}
          aria-label="Remove Item"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#13682B',
          }}
        />
      </header>
      <If condition={action?.value === ACTIONS.CLONE && showGeoAudienceCheck}>
        <div>
          <CheckBox
            onChange={handleGeoAudienceCheckBoxClick}
            name="Enable Geographical Search"
            label="Enable Geographical Search"
            checked={geoSearch}
            labelStyle={{ color: '#13682b', gap: '3px', marginLeft: '3px', marginBottom: '6px' }}
            iconStyle={{ width: '20px' }}
          />
        </div>
      </If>
      <div className="batch-card__meta">
        <div>
          <Icon icon="info" type="outline" color="#666666" width="16px" height="16px" />
          <span>{status}</span>
        </div>
        <div>
          <Icon icon="society" type="outline" color="#666666" width="16px" height="16px" />
          <span>{format('metric', size)}</span>
        </div>
        <div style={{ justifySelf: 'flex-end' }}>
          <Icon icon="calendar" type="outline" color="#666666" width="16px" height="16px" />
          <span>{date}</span>
        </div>
        <div style={{ gridArea: 'user' }}>
          <Icon icon="contacts" type="outline" color="#666666" width="16px" height="16px" />
          <span>{user}</span>
        </div>
      </div>
    </div>
  )
}

const BatchSidebar = ({ onConfirm, actions, CustomCard, showGeoAudienceCheck, entityLabel }) => {
  const [action, setAction] = useState(null)
  const [items, setItems] = useState([])
  // itemsRef mirrors `items` synchronously so the once-registered event handler
  // can read/compute from the current selection without a stale closure.
  const itemsRef = useRef([])
  const commitItems = (next) => {
    const value = typeof next === 'function' ? next(itemsRef.current) : next
    itemsRef.current = value
    setItems(value)
  }
  // when > 0, an "all pages" selection is active; holds its current count
  const [allPagesCount, setAllPagesCount] = useState(0)
  const allPagesRef = useRef(0)
  const setAllPages = (count) => {
    allPagesRef.current = count
    setAllPagesCount(count)
  }
  // true only while the all-pages selection still covers every record; a single
  // deselect drops it to false so the summary wording stops saying "all pages"
  const [allPagesFull, setAllPagesFull] = useState(false)

  const editNotice =
    action?.value === ACTIONS.BULK_EDIT && items.some((item) => item.status === 'processed')
  // const batchAttributesDisabled =
  //   action?.value === ACTIONS.BATCH_PREBUILT_ATTRIBUTE_SETUP &&
  //   (items.length !== 1 || items[0].status !== 'draft')

  const handleSelectionEvent = ({ action, items, entities, allPages, count }) => {
    switch (action) {
      case CHECKBOX_ACTION.ADD:
        commitItems((prevItems) => [...prevItems, ...entities])
        break
      case CHECKBOX_ACTION.DELETE: {
        // compute the surviving selection outside of any state updater, then
        // update the item list and the all-pages count together at one level
        const remaining = itemsRef.current.filter((prev) => !items.includes(prev._id))
        commitItems(remaining)
        // in all-pages mode keep the rest of the selection and just adjust the
        // count summary, rather than dropping everything on a single deselect;
        // the selection is no longer the full set once a row is removed
        if (allPagesRef.current > 0) {
          setAllPages(remaining.length)
          setAllPagesFull(false)
        }
        break
      }
      case CHECKBOX_ACTION.SELECT_ALL_PAGES:
        setAllPages(allPages ? count : 0)
        setAllPagesFull(allPages)
        commitItems(entities)
        break
    }
  }

  useEffect(() => {
    const event = Events.addListener('ListView-selection', handleSelectionEvent)
    return () => event.remove()
  }, [])

  // expose control to clear sidebar and checkboxes
  useEffect(() => {
    const event = Events.addListener('ListView-selection--clear', () => {
      handleClear()
    })
    return () => event.remove()
  }, [])

  const handleRemove = (id) => () => {
    commitItems((prevItems) => prevItems.filter((prevItem) => prevItem._id !== id))
    Events.emit('Table-handle-selected', { action: CHECKBOX_ACTION.DELETE, ids: [id] })
  }

  const handleActionChange = (selectedAction) => {
    setAction(selectedAction)
  }

  const handleGeoSearchClicked = (id, status) => {
    const updateItems = [...itemsRef.current]
    const itemToUpdate = updateItems.find((item) => item._id === id)
    if (itemToUpdate) {
      itemToUpdate.geo_audience = status
    }
    commitItems(updateItems)
  }

  const handleConfirm = () => {
    onConfirm(action.value, items)
  }

  // in "all pages" mode we show a count summary instead of a card per record
  const displayedItems = allPagesCount > 0 ? [] : items
  // only claim "across all pages" while the selection still covers every record
  const allPagesSummary = allPagesFull
    ? `${entityLabel} selected across all pages.`
    : `${entityLabel} selected.`

  const handleClear = () => {
    Events.emit('Table-handle-selected', {
      action: CHECKBOX_ACTION.DELETE,
      ids: itemsRef.current.map(({ _id }) => _id),
    })
    commitItems([])
    setAction(null)
    setAllPages(0)
    setAllPagesFull(false)
  }

  const options = [
    { value: ACTIONS.APPROVE, label: 'Approve' },
    { value: ACTIONS.ARCHIVE, label: 'Archive' },
    // { value: ACTIONS.BATCH_PREBUILT_ATTRIBUTE_SETUP, label: 'Batch Prebuilt Attributes Setup' },
    { value: ACTIONS.CLONE, label: 'Clone' },
    { value: ACTIONS.CLONE_WITH_AUDIENCES, label: 'Clone With Audiences' },
    { value: ACTIONS.CLONE_RESOURCE_OPTIMIZED, label: 'Clone (Resource Optimized)' },
    { value: ACTIONS.DELETE, label: 'Delete' },
    { value: ACTIONS.BULK_EDIT, label: 'Bulk Edit' },
    { value: ACTIONS.EXPORT, label: 'Export' },
    { value: ACTIONS.MERGE, label: 'Merge' },
  ].filter(({ value }) => actions.includes(value))

  return (
    <div className={classNames('batch-sidebar', { 'batch-sidebar--closed': items.length === 0 })}>
      <div className="batch-sidebar__content mac-scrollbar">
        <If condition={allPagesCount > 0}>
          <div className="batch-sidebar__all-pages-summary">
            <p className="batch-sidebar__all-pages-count">{allPagesCount}</p>
            <p>{allPagesSummary}</p>
          </div>
        </If>
        <For each="item" of={displayedItems}>
          {CustomCard ? (
            <CustomCard key={item._id} item={item} handleRemove={handleRemove(item._id)} />
          ) : (
            <BatchCard
              id={item._id}
              action={action}
              key={item._id}
              title={item.name?.split('>').at(-1).trim()}
              status={item.status}
              size={item.actual_size}
              date={item.updated_at?.split('T')[0]}
              user={item.created_by?.email}
              handleRemove={handleRemove(item._id)}
              showGeoAudienceCheck={showGeoAudienceCheck}
              geoAudience={item?.geo_audience}
              handleGeoSearchClicked={handleGeoSearchClicked}
            />
          )}
        </For>
      </div>
      <footer className="batch-sidebar__footer">
        <Select
          name="Action"
          label="Select Action:"
          placeholder="Select"
          onChange={handleActionChange}
          options={options}
          value={action}
          menuPlacement="top"
          useReactSelect
          styles={{
            menuList: (base) => ({
              ...base,
              minHeight: 30 + 40 * options.length, // quick way of solving the actions menu not growing properly
            }),
          }}
        />

        <Button onClick={handleConfirm} disabled={!action || !items.length}>
          Ok
        </Button>
        <If condition={editNotice}>
          <div style={{ gridColumn: '1/-1' }}>
            Bulk editing processed audiences will get updated in all next audience deliveries
            automatically
          </div>
        </If>
        {/* <If condition={batchAttributesDisabled}>
          <div style={{ gridColumn: '1/-1' }}>⚠️ Select only one draft audience</div>
        </If> */}
        <Button onClick={handleClear} theme={BUTTON_THEME.SECONDARY} style={{ gridColumn: '1/-1' }}>
          Cancel
        </Button>
      </footer>
    </div>
  )
}

BatchSidebar.propTypes = {
  actions: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  // noun shown in the "all pages" summary, e.g. "audiences" / "campaigns"
  entityLabel: PropTypes.string,
}

BatchSidebar.defaultProps = {
  actions: [],
  onConfirm: () => {},
  entityLabel: 'items',
}

export default BatchSidebar
